import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import { FallbackImage, Input, Modal, Render, Select } from '../../../components';
import { GeneralService, MaintenanceService } from '../../../services';
import { sanitizeToNumbers, classNames, formatAmount, clone, sanitizeToDecimal, Constants } from '../../../utils';
import { BackIcon, CameraIcon, CloseIcon } from '../../../assets/icons';
import ModalCreateSupply from './modal-create-supply';
import ModalCreateSupplier from './modal-create-supplier';

const STEP = {
  STEP1: 1,
  STEP2: 2,
  STEP3: 3,
};

const MODAL = {
  NONE: 0,
  SUPPLY: 1,
  SUPPLIER: 2,
}

const ReportMaintenance = () => {
  const navigate = useNavigate();
  const [modal, setModal] = useState(MODAL.NONE);
  const [isGasDisabled, setisGasDisabled] = useState(true);
  const { form, getSuppliers, getSupplies, isLoading, nextStep, onAdd, onChange, onRemove, selects, setStep, step } = useForm();

  return (
    <>
      <Modal isOpen={modal === MODAL.SUPPLY} onClose={() => setModal(MODAL.NONE)}>
        <ModalCreateSupply onClose={() => setModal(MODAL.NONE)} onSuccess={getSupplies} />
      </Modal>

      <Modal isOpen={modal === MODAL.SUPPLIER} onClose={() => setModal(MODAL.NONE)}>
        <ModalCreateSupplier onClose={() => setModal(MODAL.NONE)} onSuccess={getSuppliers} />
      </Modal>

      <div className="flex items-center px-6 py-4">
        <button
          type="button"
          className="p-1.5 rounded-full hover:bg-gray5 transition-colors"
          onClick={() => navigate('/maintenance/plan')}
        >
          <img src={BackIcon} className="w-6 h-6 object-contain brightness-0 " alt="Volver" />
        </button>
        <h2 className="text-2xl font-bold text-text ml-4">Reportar mantenimiento</h2>
      </div>

      <div className="w-[48rem] mx-auto bg-white rounded-2xl shadow-xl pb-6">
        <div className="flex justify-center items-center text-orange font-bold text-lg py-6">
          <span>Paso 1</span>
          <hr className={classNames('w-8 mx-4', step >= STEP.STEP2 ? 'border-orange' : 'border-gray6')} />
          <span className={classNames(step >= STEP.STEP2 ? 'text-orange' : 'text-gray6')}>
            Paso 2
          </span>
          <hr className={classNames('w-8 mx-4', step === STEP.STEP3 ? 'border-orange' : 'border-gray6')} />
          <span className={classNames(step === STEP.STEP3 ? 'text-orange' : 'text-gray6')}>
            Paso 3
          </span>
        </div>

        <Render condition={step === STEP.STEP1}>
          <div>
            <h2 className="text-center text-black text-xl font-bold mb-6">Datos de mantenimiento</h2>

            <div className="grid grid-cols-2 gap-4 px-6">
              <Input
                label="Categoría de mantenimiento"
                value={form.category?.name}
                onValueChange={() => {}}
                disabled
              />
              <Input
                label="Subcategoría de mantenimiento"
                value={form.subcategory?.name}
                onValueChange={() => {}}
                disabled
              />
              <Input
                type="date"
                label="Fecha al finalizar el mantenimiento"
                value={form.finished_at}
                onValueChange={v => onChange('finished_at', v)}
              />
              <Input
                label="Kilometraje (muestra la última recarga)"
                className={{ wrapper: 'pr-1' }}
                value={form.mileage}
                onValueChange={v => onChange('mileage', sanitizeToNumbers(v))}
                disabled={isGasDisabled}
                append={
                  <>
                    <Render condition={!isGasDisabled}>
                      <button
                        className="text-text py-1 rounded-full"
                        onClick={() => {
                          setisGasDisabled(!isGasDisabled);
                          onChange('mileage', form.mileage_original);
                        }}
                      >
                        Cancelar
                      </button>
                    </Render>
                    <button
                      className="bg-[#396AFF] text-white px-4 py-1 rounded-full font-semibold"
                      onClick={() => {
                        onChange('mileage_original', form.mileage);
                        setisGasDisabled(!isGasDisabled);
                      }}
                    >
                      { isGasDisabled ? 'Editar' : 'Listo' }
                    </button>
                  </>
                }
              />
            </div>

            <div className="flex justify-center mt-2 px-6 py-4 gap-3">
              <button
                type="button"
                className="px-6 py-2 bg-red text-white font-semibold rounded-full"
                onClick={nextStep}
                disabled={isLoading}
              >
                Siguiente
              </button>
            </div>
          </div>
        </Render>

        <Render condition={step === STEP.STEP2}>
          <div className="relative">
            <div className="flex items-center mb-6 justify-center">
              <button className="absolute left-6" onClick={() => setStep(STEP.STEP1)}>
                <img src={BackIcon} className="w-5 h-5 object-contain invert" alt="Volver" />
              </button>

              <h2 className="text-center text-black text-xl font-bold">Insumos o repuestos</h2>
            </div>

            <div className="grid grid-cols-2 gap-4 px-6">
              <div>
                <p className="mb-1 text-text">Nombre de Insumos o repuestos</p>
                <Select
                  items={selects.supplies}
                  value={form.supply.supply_id}
                  onValueChange={v => onChange('supply.supply_id', v)}
                />
                <button
                  className="bg-orange text-white px-2 py-1 rounded-full text-sm mt-2"
                  onClick={() => setModal(MODAL.SUPPLY)}
                >
                  + Agregar insumos o repuestos
                </button>
              </div>
              <Input
                label="Cantidad"
                value={form.supply.quantity}
                onValueChange={v => onChange('supply.quantity', v)}
              />
              <div>
                <p className="mb-1 text-text">Unidad de medida</p>
                <Select
                  items={selects.measuring_units}
                  value={form.supply.measuring_unit_id}
                  onValueChange={v => onChange('supply.measuring_unit_id', v)}
                />
              </div>
              <Input
                label="Costo $"
                value={form.supply.cost}
                onValueChange={v => onChange('supply.cost', sanitizeToDecimal(v))}
              />
              <div>
                <p className="mb-1 text-text">Proveedor</p>
                <Select
                  items={selects.suppliers}
                  value={form.supply.supplier_id}
                  onValueChange={v => onChange('supply.supplier_id', v)}
                />
                <button
                  className="bg-orange text-white px-2 py-1 rounded-full text-sm mt-2"
                  onClick={() => setModal(MODAL.SUPPLIER)}
                >
                  + Agregar proveedor
                </button>
              </div>
            </div>

            <div className="flex justify-center mt-2 px-6 py-4 gap-3">
              <button
                type="button"
                className="px-6 py-2 bg-green text-white font-semibold rounded-full"
                onClick={onAdd}
              >
                Agregar
              </button>
            </div>

            <div className="bg-gray5 space-y-4 p-4">
              {form.supplies.map((item, index) => (
                <div key={index} className="grid grid-cols-[1fr_1fr_1fr_1fr_1fr_4rem] gap-2 p-2 bg-white rounded-lg border-default items-start text-sm">
                  <div>
                    <label className="text-default">Nombre</label>
                    <p>{ item?.supply?.name }</p>
                  </div>
                  <div>
                    <label className="text-default">Cantidad</label>
                    <p>{ item?.quantity }</p>
                  </div>
                  <div>
                    <label className="text-default">Uni. Medida</label>
                    <p>{ item?.measuring_unit?.name }</p>
                  </div>
                  <div>
                    <label className="text-default">Costo ($)</label>
                    <p>{ formatAmount(item?.total) }</p>
                  </div>
                  <div>
                    <label className="text-default">Proveedor</label>
                    <p>{ item?.supplier?.name }</p>
                  </div>
                  <div className="flex h-full">
                    <button className="text-sm text-red" onClick={() => onRemove(index)}>
                      Eliminar
                    </button>
                  </div>
                </div>
              ))}

              <Render condition={!form.supplies.length}>
                <p className="text-center  text-text py-4">No ha llenado los campos</p>
              </Render>
            </div>

            <div className="flex justify-center px-6 py-4 gap-3">
              <button
                type="button"
                className="px-6 py-2 bg-red text-white font-semibold rounded-full"
                onClick={nextStep}
                disabled={isLoading}
              >
                Siguiente
              </button>
            </div>
          </div>
        </Render>

        <Render condition={step === STEP.STEP3}>
          <div className="relative">
            <div className="flex items-center mb-6 justify-center">
              <button className="absolute left-6" onClick={() => setStep(STEP.STEP2)}>
                <img src={BackIcon} className="w-5 h-5 object-contain invert" alt="Volver" />
              </button>

              <div>
                <h2 className="text-center text-black text-xl font-bold">Evidencia</h2>
                <p className="text-center">Opcional</p>
              </div>
            </div>

            <div className="w-1/2 mx-auto">
              <p className="text-text">Tome o suba imagen de la falla</p>
              <Render condition={!form.evidence}>
                <label className="bg-[#000] text-white font-semibold flex items-center justify-center w-full rounded-full py-2 gap-2 cursor-pointer">
                  <span>Camara</span>
                  <img src={CameraIcon} className="w-6 h-6 object-contain" alt="" />

                  <input
                    type="file"
                    hidden
                    onChange={e => {
                      const file = e.target.files[0];
                      if (!file) return;
                      onChange('evidence', file);
                      onChange('evidence_blob', URL.createObjectURL(file));
                    }}
                    accept=".jpg,.jpeg,.png"
                  />
                </label>
              </Render>
              <Render condition={form.evidence}>
                <div className="w-36 h-36 bg-gray5 rounded-lg mx-auto flex items-center justify-center relative">
                  <FallbackImage
                    src={form.evidence_blob}
                    placeholder={CameraIcon}
                    className="w-full h-full object-cover"
                    placeholderClassName="w-12 h-12"
                    alt="Evidencia"
                  />
                  <button
                    className="absolute top-2 right-2"
                    onClick={() => {
                      onChange('evidence', null);
                      onChange('evidence_blob', null);
                    }}
                  >
                    <img src={CloseIcon} className="w-4 h-4 object-contain" alt="" />
                  </button>
                </div>
              </Render>

              <p className="text-xl font-bold mt-4">Observaciones</p>
              <textarea
                className="w-full h-24 border border-default rounded-lg p-2"
                placeholder="Opcional"
                value={form.observations}
                onChange={v => onChange('observations', v.target.value)}
              />
            </div>

            <div className="flex justify-center mt-2 px-6 py-4 gap-3">
              <button
                type="button"
                className="px-6 py-2 bg-red text-white font-semibold rounded-full"
                onClick={nextStep}
                disabled={isLoading}
              >
                Guardar
              </button>
            </div>
          </div>
        </Render>
      </div>
    </>
  );
};

const useForm = () => {
  const initialSupply = {
    supply_id: '',
    quantity: '',
    measuring_unit_id: '',
    cost: '',
    total: '',
    supplier_id: '',
  };

  const getInitial = (data) => ({
    id: data?.id || '',
    vehicle: data?.vehicle || {},
    category: data?.category || {},
    subcategory: data?.subcategory || {},
    mileage: String(Number(data?.mileage).toFixed(0)) || '',
    mileage_original: String(Number(data?.mileage).toFixed(0)) || '',
    observations: '',
    cost_total: '',
    cost_conversion: '',
    cost_total_local: '',
    finished_at: '',
    supply: {
      supply_id: '',
      quantity: '',
      measuring_unit_id: '',
      cost: '',
      supplier_id: '',
    },
    supplies: [],
    evidence: '',
    evidence_blob: null,
    hasFile: true,
  });

  const { id } = useParams();
  const navigate = useNavigate();
  const [step, setStep] = useState(STEP.STEP1);
  const [form, setForm] = useState(() => getInitial());
  const [selects, setSelects] = useState({
    supplies: [],
    measuring_units: [],
    suppliers: [],
  });
  const [isLoading, setIsLoading] = useState(false);

  const fetchData = async () => {
    const decodedId = Number(atob(id));

    try {
      const item = await MaintenanceService.moderator.findOne(decodedId);
      if (!item) {
        toast.error('No se encontró el mantenimiento');
        return navigate('/maintenance/plan', { replace: true });
      }
      if (item.vehicle?.belong_to === Constants.BELONGS_TO.OWNER) {
        toast.error('El vehículo tiene propietario, no puede enviar a plan de mantenimiento');
        return navigate('/maintenance/plan', { replace: true });
      }
      if (item.status !== Constants.MAINTENANCE.STATUS.IN_PROCESS) {
        toast.error('Estado de mantenimiento inválido');
        return navigate('/maintenance/plan', { replace: true });
      }
      setForm(getInitial(item));

    } catch (error) {
      console.log(error)
    }
  };

  const getMeasuringUnits = async () => {
    try {
      const measuring_units = await GeneralService.getMeasuringUnits();
      setSelects(s => ({ ...s, measuring_units: measuring_units.map(x => ({ value: x.id, label: x.name })) }));

    } catch (error) {
      console.log(error);
    }
  };

  const getSupplies = async () => {
    try {
      const supplies = await GeneralService.getSupplies();
      setSelects(s => ({ ...s, supplies: supplies.map(x => ({ value: x.id, label: x.name })) }));

    } catch (error) {
      console.log(error);
    }
  };

  const getSuppliers = async () => {
    try {
      const suppliers = await GeneralService.getSuppliers();
      setSelects(s => ({ ...s, suppliers: suppliers.map(x => ({ value: x.id, label: x.name })) }));

    } catch (error) {
      console.log(error);
    }
  };

  const getSelects = async () => {
    getMeasuringUnits();
    getSupplies();
    getSuppliers();
  };

  const onChange = (target, value) => {
    if (String(target).includes('.')) {
      const split = target.split('.');
      const obj = split[0];
      const key = split[1];
      setForm(s => ({ ...s, [obj]: { ...s[obj], [key]: value } }));

    } else {
      setForm(s => ({ ...s, [target]: value }));
    }
  };

  const onAdd = () => {
    if (!isValidForm(true)) return;

    const measuring_unit = selects.measuring_units.find(x => +x.value === +form.supply.measuring_unit_id);
    const supply = selects.supplies.find(x => +x.value === +form.supply.supply_id);
    const supplier = selects.suppliers.find(x => +x.value === +form.supply.supplier_id);

    const supplies = form.supplies.concat([{
      ...form.supply,
      total: Number(form.supply.cost) * Number(form.supply.quantity),
      measuring_unit: {
        name: measuring_unit?.label,
      },
      supply: {
        name: supply?.label,
      },
      supplier: {
        name: supplier?.label,
      },
    }]);
    onChange('supplies', supplies);
    onChange('supply', clone(initialSupply));
  }

  const onRemove = (index) => {
    const supplies = form.supplies.filter((_, i) => i !== index);
    onChange('supplies', supplies);
  }

  const nextStep = () => {
    if (!isValidForm()) return;

    if (step === STEP.STEP1) return setStep(STEP.STEP2);
    if (step === STEP.STEP2) return setStep(STEP.STEP3);
    if (step === STEP.STEP3) return submit();
  };

  const isValidForm = (subForm = false) => {
    const onError = (msg) => {
      toast.error(msg);
      return false;
    }

    if (step === STEP.STEP1) {
      if (!form.finished_at)
        return onError('La fecha de finalización es requerida');

      if (!Number(form.mileage))
        return onError('El kilometraje es requerido');
    }

    if (step === STEP.STEP2) {
      if (subForm) {
        if (!form.supply.supply_id)
          return onError('El insumo o repuesto es requerido');

        if (!form.supply.quantity)
          return onError('La cantidad es requerida');

        if (!form.supply.measuring_unit_id)
          return onError('La unidad de medida es requerida');

        if (!form.supply.cost)
          return onError('El costo es requerido');

        if (!form.supply.supplier_id)
          return onError('El proveedor es requerido');

      } else {
        if (!form.supplies.length)
          return onError('Debe agregar al menos un insumo o repuesto');
      }
    }

    return true;
  };

  const submit = async () => {
    if (!isValidForm()) return;
    setIsLoading(true);
    try {
      const { photo_blob, vehicle_photo_blob, ...formData } = form;
      const payload = {
        ...formData,
        supplies: JSON.stringify(form.supplies),
        category: undefined,
        subcategory: undefined,
        supply: undefined,
        vehicle: undefined,
        cost_total: form.supplies.reduce((acc, x) => acc + +x.total, 0),
      }

      await MaintenanceService.moderator.create(payload);
      toast.success('El reporte de mantenimiento se ha guardado exitosamente');
      navigate('/maintenance/plan', { replace: true });

    } catch (error) {
      console.log(error)
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (step !== STEP.STEP1) return;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step]);

  useEffect(() => {
    fetchData();
    getSelects();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return {
    form,
    getMeasuringUnits,
    getSuppliers,
    getSupplies,
    isLoading,
    nextStep,
    onAdd,
    onChange,
    onRemove,
    selects,
    setSelects,
    setStep,
    step,
    submit,
  };
};

export default ReportMaintenance;
